import { t } from '../i18n';
/**
 * Messages vocaux, sans Firebase Storage.
 *
 * Meme compromis que pour les photos (`utils/image.ts`) : le projet n'a
 * pas de serveur et le forfait n'ouvre pas Storage, donc l'enregistrement
 * est encode ici et range dans la base sous forme de data URL. Cela ne
 * tient que parce qu'on borne severement — un debit de parole, mono, et
 * une duree plafonnee.
 *
 * C'est un compromis assume, pas une bonne pratique. Le jour ou Storage
 * s'ouvre, seul ce fichier change : le reste de l'application ne manipule
 * qu'une URL, une duree et une silhouette.
 */

/** Au-dela, on arrete tout seul : un vocal n'est pas un podcast. */
export const MAX_VOICE_SECONDS = 90;

/**
 * Debit d'encodage.
 *
 * 24 kb/s en mono suffisent largement pour de la voix — c'est l'ordre de
 * grandeur d'un appel telephonique — et donnent environ 3 ko par seconde
 * une fois en data URL. Quatre-vingt-dix secondes tiennent alors sous le
 * plafond, avec de la marge.
 */
const BITS_PER_SECOND = 24_000;

/**
 * Plafond de stockage, sur la data URL elle-meme.
 *
 * Au-dela, l'ecriture echouerait cote base avec une erreur
 * incomprehensible : mieux vaut refuser ici, avec une phrase.
 */
const MAX_STORED_BYTES = 700_000;

/** Nombre de barres de la silhouette. Assez pour lire un rythme. */
const PEAKS = 40;

/**
 * Les formats, par ordre de preference.
 *
 * WebKit — donc l'application iOS — n'enregistre qu'en MPEG-4/AAC ;
 * Chrome et Firefox preferent Opus, plus efficace. On prend le premier
 * que le navigateur reconnait plutot que d'en imposer un.
 */
const FORMATS = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];

export class VoiceError extends Error {}

/** Ce qu'un vocal porte, une fois enregistre. */
export interface VoiceClip {
  audioUrl: string;
  durationMs: number;
  /** Silhouette de l'onde, de 0 a 100. Voir `peaksOf`. */
  peaks: number[];
}

/** Le micro est-il utilisable ici ? */
export function canRecordVoice(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/** Un enregistrement en cours. */
export interface VoiceRecording {
  /** Arrete, encode, et rend le vocal. */
  stop: () => Promise<VoiceClip>;
  /** Abandonne : rien n'est encode, le micro est rendu. */
  cancel: () => void;
  /** Suspend la prise. Le micro reste ouvert, rien ne s'enregistre. */
  pause: () => void;
  /** Reprend la ou l'on s'etait arrete. */
  resume: () => void;
  /** Suspendu en ce moment ? */
  enPause: () => boolean;
  /**
   * Le temps reellement enregistre, en millisecondes.
   *
   * Ce n'est pas le temps ecoule depuis le debut : une pause ne compte
   * pas. C'est cette mesure-la que porte le vocal envoye, sinon un
   * enregistrement suspendu deux minutes s'annoncerait long de deux
   * minutes pour trois secondes de voix.
   */
  duree: () => number;
  /**
   * Le niveau sonore a cet instant, de 0 a 100.
   *
   * Lu a la demande plutot que pousse : c'est l'ecran qui decide de son
   * rythme d'affichage, et un abonnement rendrait la mesure vivante meme
   * quand personne ne la regarde.
   */
  level: () => number;
}

/**
 * Ouvre le micro et commence a enregistrer.
 *
 * La permission est demandee par le systeme au premier appel ; un refus
 * remonte ici en message francais plutot qu'en `NotAllowedError`.
 */
export async function startRecording(): Promise<VoiceRecording> {
  if (!canRecordVoice()) {
    throw new VoiceError(t.composants.voix.appareil);
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      // Le traitement du navigateur fait le gros du travail : sans lui,
      // un vocal enregistre dans la rue est inecoutable.
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch {
    throw new VoiceError(t.composants.voix.micro);
  }

  const mimeType = FORMATS.find((format) => MediaRecorder.isTypeSupported(format));
  const recorder = new MediaRecorder(stream, {
    ...(mimeType ? { mimeType } : {}),
    audioBitsPerSecond: BITS_PER_SECOND,
  });

  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  /*
    L'analyseur sert l'onde vivante pendant l'enregistrement. Il ecoute le
    meme flux, en derivation : il ne touche pas a ce qui est enregistre, et
    son absence — un navigateur sans `AudioContext` — ne fait perdre que
    l'animation.
  */
  const meter = openMeter(stream);

  /*
    Le temps enregistre se compte par tranches : ce qui est deja acquis,
    plus la tranche en cours. Une soustraction unique depuis le debut
    compterait les pauses.
  */
  let acquis = 0;
  let depuis = Date.now();
  recorder.start();

  const duree = () => acquis + (recorder.state === 'paused' ? 0 : Date.now() - depuis);

  /** Rendre le micro : sans cela, la pastille rouge reste allumee. */
  function release() {
    meter?.close();
    stream.getTracks().forEach((track) => track.stop());
  }

  return {
    level: () => meter?.read() ?? 0,
    duree,
    enPause: () => recorder.state === 'paused',

    pause() {
      if (recorder.state !== 'recording') return;
      acquis += Date.now() - depuis;
      recorder.pause();
    },

    resume() {
      if (recorder.state !== 'paused') return;
      depuis = Date.now();
      recorder.resume();
    },

    cancel() {
      if (recorder.state !== 'inactive') recorder.stop();
      release();
    },

    stop() {
      return new Promise<VoiceClip>((resolve, reject) => {
        recorder.onstop = () => {
          release();
          const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/mp4' });
          /*
            La duree se mesure a la montre, pas sur le fichier : un blob
            MPEG-4 issu de `MediaRecorder` ne porte pas toujours sa duree,
            et `audio.duration` y vaut alors l'infini.
          */
          void encode(blob, duree()).then(resolve, reject);
        };

        if (recorder.state === 'inactive') {
          reject(new VoiceError(t.composants.voix.interrompu));
          return;
        }
        recorder.stop();
      });
    },
  };
}

/**
 * Un analyseur branche en derivation sur le micro.
 *
 * On lit le signal dans le temps plutot que son spectre : une onde de
 * parole se raconte par son volume, et un spectre de frequences ne
 * donnerait qu'une bouillie a cette taille d'affichage.
 */
function openMeter(stream: MediaStream): { read: () => number; close: () => void } | null {
  try {
    const context = new (window.AudioContext ?? window.webkitAudioContext)();
    const analyser = context.createAnalyser();
    // Court : on veut suivre la voix, pas la moyenner sur une seconde.
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);

    const frame = new Uint8Array(analyser.fftSize);

    return {
      read() {
        analyser.getByteTimeDomainData(frame);

        // Ecart quadratique moyen autour du silence (128), en 0..1.
        let sum = 0;
        for (const sample of frame) {
          const offset = (sample - 128) / 128;
          sum += offset * offset;
        }
        const rms = Math.sqrt(sum / frame.length);

        /*
          La racine etale le bas de l'echelle : une voix normale tourne
          autour de 0,05 en quadratique moyen, ce qui donnerait des barres
          minuscules et une onde plate.
        */
        return Math.min(100, Math.round(Math.sqrt(rms) * 140));
      },
      close() {
        void context.close();
      },
    };
  } catch {
    return null;
  }
}

/** Blob enregistre -> vocal pret a etre envoye. */
async function encode(blob: Blob, durationMs: number): Promise<VoiceClip> {
  if (durationMs < 700) {
    throw new VoiceError(t.composants.voix.tropCourt);
  }

  const buffer = await blob.arrayBuffer();
  const [audioUrl, peaks] = await Promise.all([toDataUrl(blob), peaksOf(buffer)]);

  if (audioUrl.length > MAX_STORED_BYTES) {
    throw new VoiceError(t.composants.voix.tropLong);
  }

  return { audioUrl, durationMs, peaks };
}

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new VoiceError(t.composants.voix.illisible));
    reader.readAsDataURL(blob);
  });
}

/**
 * La silhouette de l'onde, en `PEAKS` barres de 0 a 100.
 *
 * Elle est calculee une fois, a l'envoi, et rangee avec le message :
 * decoder l'audio a chaque fois qu'une bulle s'affiche ferait ramer un
 * fil qui en contient dix.
 *
 * On prend le maximum de chaque tranche plutot que la moyenne — une
 * moyenne sur un signal centre tend vers zero et donne une ligne plate.
 * Et si le decodage echoue, on rend une silhouette neutre : la barre est
 * un decor, elle ne doit pas empecher d'envoyer.
 */
async function peaksOf(buffer: ArrayBuffer): Promise<number[]> {
  const flat = Array.from({ length: PEAKS }, () => 35);

  try {
    const context = new (window.AudioContext ?? window.webkitAudioContext)();
    const decoded = await context.decodeAudioData(buffer.slice(0));
    void context.close();

    const samples = decoded.getChannelData(0);
    const perBar = Math.floor(samples.length / PEAKS) || 1;

    const bars = Array.from({ length: PEAKS }, (_unused, index) => {
      let loudest = 0;
      for (let at = index * perBar; at < (index + 1) * perBar && at < samples.length; at += 1) {
        const level = Math.abs(samples[at]);
        if (level > loudest) loudest = level;
      }
      return loudest;
    });

    const ceiling = Math.max(...bars, 0.01);
    // Un plancher de 8 : une barre nulle laisserait un trou dans l'onde.
    return bars.map((bar) => Math.max(8, Math.round((bar / ceiling) * 100)));
  } catch {
    return flat;
  }
}

declare global {
  interface Window {
    /** Safari ancien : le contexte audio y porte encore son prefixe. */
    webkitAudioContext?: typeof AudioContext;
  }
}
